import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DesktopService } from '../services/desktopService';

// --- ICONS ---
const FolderIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6C4 4.89543 4.89543 4 6 4H9.17157C9.70201 4 10.2107 4.21071 10.5858 4.58579L12.4142 6.41421C12.7893 6.78929 13.298 7 13.8284 7H18C19.1046 7 20 7.89543 20 9V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
            fill="currentColor" fillOpacity="0.15" />
        <path d="M4 6C4 4.89543 4.89543 4 6 4H9.17157C9.70201 4 10.2107 4.21071 10.5858 4.58579L12.4142 6.41421C12.7893 6.78929 13.298 7 13.8284 7H18C19.1046 7 20 7.89543 20 9V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
            className="stroke-[1.5]" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 10H20" className="stroke-[1.5]" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
    </svg>
);
const ArrowBackIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>;
const HomeIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>;
const RefreshIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>;
const FolderPlusIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m3-3H9m4.06-7.19l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" /></svg>;
const DesktopIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" /></svg>;
const ChevronRightIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>;
const Spinner = () => <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} className="w-8 h-8 border-4 border-[var(--accent-primary)]/20 border-t-[var(--accent-primary)] rounded-full" />;

/**
 * RemoteFileBrowser — sends live `list_directory` commands to the
 * desktop agent via the device queue and renders the results.
 */
const RemoteFileBrowser = ({ userId, targetDeviceId, onSelect, onCancel, initialPath = '' }) => {
    const [currentPath, setCurrentPath] = useState(initialPath || 'root');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState([]);
    const [cache, setCache] = useState({}); // Simple path -> items cache

    /**
     * Sends a list_directory command to the device queue,
     * subscribes for the response, and returns the parsed items.
     */
    const fetchDirectory = useCallback(async (dirPath, forceRefresh = false) => {
        if (!userId || !targetDeviceId) {
            setError("No target computer selected.");
            return;
        }

        // Use cache if available and not forcing refresh
        if (!forceRefresh && cache[dirPath]) {
            setItems(cache[dirPath]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        // Clear items immediately so the screen doesn't look "stuck" on old content
        setItems([]);

        try {
            const taskId = await DesktopService.sendDesktopCommand(
                userId,
                targetDeviceId,
                { command: 'list_directory', path: dirPath }
            );

            // Wait for the agent to respond (15s timeout)
            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    unsubscribe();
                    reject(new Error("Request timed out — check agent connection."));
                }, 15000);

                const unsubscribe = DesktopService.subscribeToTask(
                    userId,
                    targetDeviceId,
                    taskId,
                    (response) => {
                        clearTimeout(timeout);
                        unsubscribe();

                        if (typeof response === 'string' && response.startsWith('Error')) {
                            setError(response);
                            setItems([]);
                        } else {
                            try {
                                const parsed = typeof response === 'string'
                                    ? JSON.parse(response)
                                    : response;

                                if (Array.isArray(parsed)) {
                                    // Sort: folders first, then alphabetical (case insensitive)
                                    const sorted = [...parsed].sort((a, b) => {
                                        if (a.type === 'folder' && b.type !== 'folder') return -1;
                                        if (a.type !== 'folder' && b.type === 'folder') return 1;
                                        return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
                                    });
                                    setItems(sorted);
                                    setCache(prev => ({ ...prev, [dirPath]: sorted }));
                                } else {
                                    setItems([]);
                                }
                            } catch {
                                setError("Failed to parse response from device.");
                                setItems([]);
                            }
                        }
                        resolve();
                    }
                );
            });
        } catch (err) {
            setError(err.message || "Failed to list directory.");
        } finally {
            setLoading(false);
        }
    }, [userId, targetDeviceId, cache]);

    // Fetch on mount and when path changes
    useEffect(() => {
        fetchDirectory(currentPath);
    }, [currentPath, fetchDirectory]);

    const handleNavigate = (folderPath) => {
        if (folderPath === currentPath) return;
        setHistory(prev => [...prev, currentPath]);
        setCurrentPath(folderPath);
    };

    const handleUp = () => {
        if (history.length > 0) {
            const prev = history[history.length - 1];
            setHistory(h => h.slice(0, -1));
            setCurrentPath(prev);
        } else if (currentPath !== 'root') {
            // Try to logic out the parent path
            const parts = currentPath.split(/[/\\]/).filter(Boolean);
            if (parts.length > 1) {
                const parent = currentPath.substring(0, currentPath.lastIndexOf(parts[parts.length - 1]) - 1);
                setCurrentPath(parent || 'root');
            } else {
                setCurrentPath('root');
            }
        }
    };

    const handleHome = () => {
        setCurrentPath('root');
    };

    const handleRefresh = () => {
        fetchDirectory(currentPath, true);
    };

    const handleCreateFolder = async () => {
        if (!currentPath || currentPath === 'root') return;
        const name = window.prompt("Enter new folder name:");
        if (!name) return;

        setLoading(true);
        try {
            await DesktopService.createFolder(userId, targetDeviceId, name, currentPath);
            fetchDirectory(currentPath, true); // Refresh including cache
        } catch (e) {
            alert("Error creating folder: " + e.message);
            setLoading(false);
        }
    };

    const breadcrumbs = useMemo(() => {
        if (currentPath === 'root') return [{ name: 'Allowed Locations', path: 'root' }];
        const parts = currentPath.split(/[/\\]/).filter(Boolean);
        const crumbs = [{ name: 'Root', path: 'root' }];
        let runningPath = currentPath.startsWith('/') ? '/' : '';

        parts.forEach((part, i) => {
            runningPath += (i > 0 && !runningPath.endsWith('/') ? '/' : '') + part;
            crumbs.push({ name: part, path: runningPath });
        });

        // Show only last 3 if too many
        if (crumbs.length > 4) {
            return [crumbs[0], { name: '...', path: null }, ...crumbs.slice(-2)];
        }
        return crumbs;
    }, [currentPath]);

    const containerVariants = {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10 }
    };

    return (
        <div className="flex flex-col h-full bg-transparent overflow-hidden">
            {/* Toolbar / Breadcrumbs */}
            <div className="flex items-center gap-4 px-8 py-4 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]/30 backdrop-blur-md">
                <div className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1">
                    {breadcrumbs.map((crumb, idx) => (
                        <React.Fragment key={idx}>
                            <button
                                onClick={() => crumb.path && setCurrentPath(crumb.path)}
                                disabled={loading || !crumb.path || crumb.path === currentPath}
                                className={`px-2 py-1 rounded-md text-sm font-medium transition-all ${crumb.path === currentPath
                                    ? 'text-[var(--text-main)] bg-[var(--bg-tertiary)]'
                                    : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)]/50'
                                    } ${!crumb.path ? 'cursor-default' : 'cursor-pointer'}`}
                            >
                                {crumb.name}
                            </button>
                            {idx < breadcrumbs.length - 1 && (
                                <ChevronRightIcon className="w-3 h-3 text-[var(--text-muted)]/30 shrink-0" />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {currentPath !== 'root' && (
                        <button
                            onClick={handleCreateFolder}
                            disabled={loading}
                            className="p-2 text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 rounded-xl transition-all disabled:opacity-30"
                            title="New Folder"
                        >
                            <FolderPlusIcon className="w-5 h-5" />
                        </button>
                    )}
                    <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-2 text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)] rounded-xl transition-all disabled:opacity-30"
                        title="Refresh"
                    >
                        <RefreshIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar Shortcuts */}
                <div className="w-52 border-r border-[var(--border-subtle)] bg-[var(--bg-secondary)]/10 px-4 py-8 flex flex-col gap-1.5 shrink-0">
                    <p className="px-3 mb-3 text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest opacity-50">Quick Access</p>
                    <button onClick={handleHome} className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all text-sm font-medium ${currentPath === 'root' ? 'bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] shadow-sm' : 'text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-main)]'}`}>
                        <HomeIcon className="w-4 h-4" /> Root
                    </button>
                    <button onClick={() => handleNavigate(window.localStorage.getItem('last_path_desktop') || '~/Desktop')} className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all text-sm font-medium text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-main)]">
                        <DesktopIcon className="w-4 h-4" /> Desktop
                    </button>
                </div>

                {/* List Area */}
                <div className="flex-1 overflow-y-auto custom-scrollbar relative bg-[var(--bg-main)]/5">
                    <AnimatePresence mode="wait">
                        {loading ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[var(--bg-main)]/50 backdrop-blur-[4px]"
                            >
                                <Spinner />
                                <div className="mt-5 text-center space-y-1">
                                    <p className="text-sm font-bold text-[var(--text-main)] animate-pulse">Navigating to new location</p>
                                    <p className="text-[10px] text-[var(--text-muted)] font-bold uppercase tracking-widest opacity-60">Scanning remote directory...</p>
                                </div>
                            </motion.div>
                        ) : null}
                    </AnimatePresence>

                    {error && (
                        <div className="m-8 p-5 bg-red-500/5 border border-red-500/20 rounded-3xl text-red-500 text-xs font-semibold text-center shadow-xl shadow-red-500/5">
                            {error}
                        </div>
                    )}

                    {!loading && items.length === 0 && !error && (
                        <div className="flex flex-col items-center justify-center h-full text-[var(--text-muted)] opacity-50">
                            <FolderIcon className="w-16 h-16 mb-4 opacity-10" />
                            <p className="text-sm font-medium">Empty Folder</p>
                        </div>
                    )}

                    <motion.div
                        key={currentPath}
                        variants={containerVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.15 }}
                        className="flex flex-col gap-1 p-6"
                    >
                        {items.map((item, index) => (
                            <button
                                key={item.path || index}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (item.type === 'folder') handleNavigate(item.path);
                                }}
                                className="group flex items-center gap-3.5 px-4 py-2 hover:bg-[rgba(var(--accent-primary-rgb),0.06)] border border-transparent hover:border-[rgba(var(--accent-primary-rgb),0.08)] transition-all text-left rounded-xl focus:outline-none relative"
                            >
                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[rgba(var(--accent-primary-rgb),0.15)] to-[rgba(var(--accent-primary-rgb),0.03)] border border-[rgba(var(--accent-primary-rgb),0.05)] shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] group-hover:from-[rgba(var(--accent-primary-rgb),0.25)] group-hover:to-[rgba(var(--accent-primary-rgb),0.08)] flex items-center justify-center shrink-0 transition-all backdrop-blur-sm">
                                    <FolderIcon className="w-4 h-4 text-[var(--accent-primary)] drop-shadow-sm" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-[13px] font-medium text-[var(--text-main)] truncate tracking-tight group-hover:text-[var(--accent-primary)] transition-colors">{item.name}</h4>
                                </div>
                                <div className="w-7 h-7 rounded-full bg-[var(--bg-tertiary)]/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all -translate-x-3 group-hover:translate-x-0">
                                    <ChevronRightIcon className="w-3 h-3 text-[var(--accent-primary)]" />
                                </div>
                            </button>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* Footer */}
            <div className="px-10 py-8 bg-[var(--bg-secondary)]/50 border-t border-[var(--border-subtle)] flex justify-end gap-6 shrink-0 backdrop-blur-xl">
                <button
                    onClick={onCancel}
                    className="px-8 py-3.5 text-sm font-bold text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)] rounded-2xl transition-all"
                >
                    Cancel
                </button>
                <button
                    onClick={() => onSelect(currentPath === 'root' ? '' : currentPath)}
                    disabled={!currentPath || currentPath === 'root'}
                    className="px-12 py-3.5 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] disabled:bg-[var(--bg-tertiary)] disabled:text-[var(--text-muted)]/30 text-slate-950 dark:text-slate-900 text-sm font-black rounded-2xl shadow-2xl shadow-[var(--accent-primary)]/30 transition-all active:scale-95 flex items-center justify-center overflow-hidden relative group"
                >
                    <span className="relative z-10">Select Folder</span>
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
            </div>
        </div>
    );
};

export default RemoteFileBrowser;
